/**
 * Back restores where you were (wave W2, Mahmoud's UAT of 2026-10-10).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The shell is one 100vh frame whose SCREEN scrolls inside `main`, not the window, so the
 * browser's own scroll restoration never sees it: Back used to land at the top of a long
 * register, the place he left lost. This keeps the screen's scroll position per history
 * entry (the router's location key, kept in the session so a reload keeps it too):
 *
 * - Back or Forward (a POP) puts the screen back where that entry was left. A register
 *   reads its rows after it mounts, so the position is re-applied as the content grows,
 *   until it is reached, a few seconds pass, or the person scrolls themselves.
 * - A new screen (a PUSH to another path) starts at the top. A change of filter on the
 *   same screen, or a REPLACE, keeps the place.
 * - A link to a section (#hash) is the page's own business.
 */

import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { NavigationType, useLocation, useNavigationType } from "react-router";

const STORE_KEY = "qualify.scroll.positions";
const KEEP = 200;
/** How long a restore keeps trying while the screen's rows arrive. */
export const RESTORE_WINDOW_MS = 4000;

function readPositions(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    const parsed: unknown = raw === null ? {} : JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, number] => typeof entry[1] === "number",
      ),
    );
  } catch {
    return {};
  }
}

function writePositions(positions: Record<string, number>): void {
  try {
    const entries = Object.entries(positions);
    sessionStorage.setItem(STORE_KEY, JSON.stringify(Object.fromEntries(entries.slice(-KEEP))));
  } catch {
    // Storage off or full: Back still works, it just starts at the top.
  }
}

/**
 * The screen's scroll container ref, with its place kept per history entry. Attach the
 * returned ref to the element that scrolls (the shell's `main`).
 */
export function useScrollRestoration(): RefObject<HTMLElement | null> {
  const container = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef<Record<string, number>>(readPositions());
  const currentKey = useRef(location.key);
  const previousPath = useRef(location.pathname);
  const restoring = useRef<{ target: number; until: number } | null>(null);

  // The browser must not fight us over the window (it has nothing to restore anyway).
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Remember the place as the person scrolls; a restore in progress is not a choice.
  useEffect(() => {
    const element = container.current;
    if (element === null) {
      return undefined;
    }
    let frame = 0;
    const onScroll = (): void => {
      if (restoring.current !== null) {
        return;
      }
      // Recorded NOW, under the entry it belongs to: a click that follows the scroll at
      // once must not find the place unrecorded. Only the session write waits a frame.
      positions.current[currentKey.current] = element.scrollTop;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        writePositions(positions.current);
      });
    };
    // The person's own wheel, touch or key ends a restore: their scroll wins.
    const takeOver = (): void => {
      restoring.current = null;
    };
    element.addEventListener("scroll", onScroll, { passive: true });
    element.addEventListener("wheel", takeOver, { passive: true });
    element.addEventListener("touchstart", takeOver, { passive: true });
    element.addEventListener("keydown", takeOver);
    return () => {
      window.cancelAnimationFrame(frame);
      element.removeEventListener("scroll", onScroll);
      element.removeEventListener("wheel", takeOver);
      element.removeEventListener("touchstart", takeOver);
      element.removeEventListener("keydown", takeOver);
    };
  }, [container]);

  // On every move between entries: restore on Back/Forward, top on a new screen.
  useLayoutEffect(() => {
    // The place being left is NOT read here: by now the next screen is in the DOM and
    // the browser has already clamped the old place to the new screen's height. The
    // scroll listener recorded it as it happened.
    const element = container.current;
    currentKey.current = location.key;
    const pathChanged = previousPath.current !== location.pathname;
    previousPath.current = location.pathname;
    if (element === null) {
      return;
    }
    if (navigationType === NavigationType.Pop) {
      const target = positions.current[location.key] ?? 0;
      restoring.current = { target, until: Date.now() + RESTORE_WINDOW_MS };
      element.scrollTop = target;
      return;
    }
    restoring.current = null;
    if (navigationType === NavigationType.Push && pathChanged && location.hash === "") {
      element.scrollTop = 0;
    }
  }, [container, location.key, location.pathname, location.hash, navigationType]);

  // A register reads its rows after it mounts: re-apply the place as the screen grows.
  useEffect(() => {
    const element = container.current;
    if (element === null) {
      return undefined;
    }
    let frame = 0;
    const step = (): void => {
      const pending = restoring.current;
      if (pending === null) {
        return;
      }
      if (Math.abs(element.scrollTop - pending.target) <= 1 || Date.now() > pending.until) {
        restoring.current = null;
        positions.current[currentKey.current] = element.scrollTop;
        writePositions(positions.current);
        return;
      }
      element.scrollTop = pending.target;
      frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [container, location.key]);

  return container;
}
